import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import InfoIcon from "@mui/icons-material/Info";
import DeleteIcon from "@mui/icons-material/Delete";
import EventIcon from "@mui/icons-material/Event";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import { Download as DownloadIcon } from "@mui/icons-material";
import { Chip, Tooltip, IconButton, CircularProgress } from "@mui/material";
import "../../assets/scss/components/WeekCalendar.scss";
import PropTypes from "prop-types";
import { Cancel } from "@mui/icons-material";
import { useNotification } from "../../context/NotificationContext";

const formatDateToISOString = (date) => {
  if (!date) return null;

  if (typeof date === "string") {
    return date.includes("T") ? date.split("T")[0] : date;
  }

  if (date instanceof Date) {
    return date.toISOString().split("T")[0];
  }

  return null;
};

// Función auxiliar para determinar si es día futuro
const isFutureDate = (dateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(dateStr);
  date.setHours(0, 0, 0, 0);
  return date > today;
};

const CapacityIndicator = ({
  capacity,
  vehicles = [],
  theme,
  selectedRouteId,
}) => {
  if (!capacity) return null;

  // Solo mostrar si la capacidad corresponde a la ruta actual
  if (selectedRouteId && capacity.operationRouteId !== selectedRouteId) {
    return null;
  }

  // Manejar días bloqueados por eventos
  if (capacity.status === "blocked_by_event") {
    return (
      <Tooltip title="Día bloqueado por evento">
        <Chip
          icon={<EventIcon fontSize="small" />}
          label="Evento"
          size="small"
          color="default"
          className={`GM__${theme}-capacity-indicator event-blocked`}
        />
      </Tooltip>
    );
  }

  // Calcular valores en tiempo real incluyendo predicciones
  const calculateRealTimeValues = () => {
    let filteredVehicles = vehicles;
    if (selectedRouteId) {
      filteredVehicles = vehicles.filter(
        (v) =>
          v.operationRouteId === selectedRouteId ||
          v.route?.id === selectedRouteId
      );
    } else {
      filteredVehicles = vehicles.filter((v) => !v.operationRouteId);
    }

    // Incluir vehículos predichos en el cálculo
    const activeVehicles = filteredVehicles.filter((v) => !v.is_cancelled);
    const cancelledVehicles = filteredVehicles.filter((v) => v.is_cancelled);

    const realScheduled = activeVehicles.length;
    const realCancelled = cancelledVehicles.length;
    const planned = capacity.planned || capacity.plannedCapacity || 0;
    const realDifference = realScheduled - planned;

    let realStatus = "no_capacity_set";
    if (planned > 0) {
      if (realDifference > 0) {
        realStatus = "over_capacity";
      } else if (realDifference < 0) {
        realStatus = "under_capacity";
      } else {
        realStatus = "at_capacity";
      }
    }

    return {
      scheduled: realScheduled,
      planned,
      cancelled: realCancelled,
      difference: realDifference,
      status: realStatus,
      isOverCapacity: realStatus === "over_capacity",
      isUnderCapacity: realStatus === "under_capacity",
    };
  };

  const realTimeCapacity = calculateRealTimeValues();

  if (realTimeCapacity.planned === 0) {
    return null;
  }

  const getStatusIcon = () => {
    if (realTimeCapacity.isOverCapacity) {
      return <ErrorOutlineIcon fontSize="small" />;
    } else if (realTimeCapacity.isUnderCapacity) {
      return <WarningAmberIcon fontSize="small" />;
    } else if (realTimeCapacity.status === "at_capacity") {
      return <CheckCircleIcon fontSize="small" />;
    }
    return null;
  };

  const getStatusColor = () => {
    if (realTimeCapacity.isOverCapacity) {
      return "error";
    } else if (realTimeCapacity.isUnderCapacity) {
      return "warning";
    } else if (realTimeCapacity.status === "at_capacity") {
      return "success";
    }
    return "default";
  };

  const getTooltipText = () => {
    const { scheduled, planned, difference } = realTimeCapacity;

    let status = "";
    if (planned === 0) {
      status = "Sin capacidad configurada";
    } else if (difference > 0) {
      status = `Excedido por ${difference}`;
    } else if (difference < 0) {
      status = `Faltan ${Math.abs(difference)}`;
    } else {
      status = "Capacidad exacta";
    }

    return `Capacidad: ${scheduled}/${planned} - ${status}`;
  };

  return (
    <Tooltip title={getTooltipText()}>
      <Chip
        icon={getStatusIcon()}
        label={`${realTimeCapacity.scheduled}/${realTimeCapacity.planned}`}
        size="small"
        color={getStatusColor()}
        className={`GM__${theme}-capacity-indicator`}
      />
    </Tooltip>
  );
};

const PredictionStatusIndicator = ({ day, theme }) => {
  if (!day.hasPredictionConflicts && !day.hasFaltantes) return null;

  return (
    <div className={`GM__${theme}-prediction-status`}>
      {day.hasPredictionConflicts && (
        <Tooltip title={`Conflictos: ${day.predictionConflicts?.join(", ")}`}>
          <Chip
            icon={<ErrorIcon fontSize="small" />}
            label="ERROR"
            size="small"
            color="error"
            variant="outlined"
            className={`GM__${theme}-prediction-error`}
          />
        </Tooltip>
      )}
      {day.hasFaltantes && (
        <Tooltip
          title={`Faltan ${day.faltantesCount} vehículos para completar capacidad`}
        >
          <Chip
            icon={<WarningIcon fontSize="small" />}
            label={`FALTANTE (${day.faltantesCount})`}
            size="small"
            color="warning"
            variant="outlined"
            className={`GM__${theme}-prediction-warning`}
          />
        </Tooltip>
      )}
    </div>
  );
};

const ExportButton = ({ 
  theme, 
  date, 
  vehicles, 
  routeId, 
  routeName,
  operationId,
  onExport 
}) => {
  const [isExporting, setIsExporting] = useState(false);
  
  const handleExport = async (e) => {
    e.stopPropagation(); // Prevenir propagación del click
    
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      await onExport(date, vehicles, routeId, routeName);
    } catch (error) {
      console.error('Error exportando:', error);
    } finally {
      setIsExporting(false);
    }
  };
  
  return (
    <Tooltip title="Exportar programación del día a Excel">
      <IconButton
        className={`GM__${theme}-export-button`}
        onClick={handleExport}
        disabled={isExporting}
        size="small"
        style={{
          position: 'absolute',
          top: '4px',
          right: '4px',
          zIndex: 10,
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          color: '#4caf50'
        }}
      >
        {isExporting ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <DownloadIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const VehicleItem = ({
  theme,
  vehicle,
  index,
  onDelete,
  onCancel,
  isPastDate,
  isToday,
  date,
}) => (
  <Draggable
    draggableId={vehicle.id.toString()}
    index={index}
    isDragDisabled={
      isPastDate || isToday || !vehicle.isActive || vehicle.isPredicted
    }
  >
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={`GM__${theme}-vehicle-item ${
          snapshot.isDragging ? "dragging" : ""
        } ${!vehicle.isActive ? "disabled" : ""} ${
          vehicle.isPredicted ? "predicted" : ""
        } ${isToday ? "today-vehicle" : ""}`}
        style={{
          ...provided.draggableProps.style,
          opacity: !vehicle.isActive ? 0.6 : vehicle.isPredicted ? 0.8 : 1,
          backgroundColor: vehicle.isPredicted ? "#f2f9e8" : null,
          border: vehicle.isPredicted ? "1px dashed #4caf50" : null,
        }}
      >
        <div className={`GM__${theme}-vehicle-plate`}>
          {vehicle.plate}
          {vehicle.isPredicted && (
            <span className="predicted-badge"> (Predicción)</span>
          )}
        </div>
        <div className={`GM__${theme}-vehicle-operation`}>
          {vehicle.operation || "No asignada"}
        </div>
        {!vehicle.isActive && (
          <div className={`GM__${theme}-tooltip`}>
            <button
              className={`GM__${theme}-icon-button GM__${theme}-vehicle-info-btn`}
            >
              <InfoIcon fontSize="small" />
            </button>
            <span className={`GM__${theme}-tooltip-text`}>
              <div className={`GM__${theme}-tooltip-title`}>
                Vehículo deshabilitado
              </div>
              <div className={`GM__${theme}-tooltip-detail`}>
                {vehicle.disabledReason || "Sin motivo especificado"}
              </div>
            </span>
          </div>
        )}
        <div className={`GM__${theme}-vehicle-actions`}>
          {!isPastDate && !isToday && !vehicle.isPredicted && (
            <button
              className={`GM__${theme}-icon-button GM__${theme}-vehicle-delete-btn`}
              onClick={() => onDelete(vehicle.id)}
              title="Eliminar vehículo"
            >
              <DeleteIcon fontSize="small" />
            </button>
          )}
          {!vehicle.isPredicted && isToday && (
            <button
              className={`GM__${theme}-icon-button GM__${theme}-vehicle-cancel-btn`}
              onClick={() =>
                onCancel(vehicle.id, {
                  vehicleId: vehicle.vehicleId,
                  plate: vehicle.plate,
                  date: date,
                  operation: vehicle.operation,
                })
              }
              title="Cancelar vehículo"
            >
              <Cancel fontSize="small" />
            </button>
          )}
        </div>
      </div>
    )}
  </Draggable>
);

const EventBadge = ({ theme, event, onClick }) => (
  <div
    className={`GM__${theme}-event-badge ${event.isFullDay ? "full-day" : ""} ${
      event.isFirstDay ? "first-day" : ""
    } ${event.isLastDay ? "last-day" : ""}`}
    onClick={() => onClick(event)}
  >
    <span className={`GM__${theme}-event-icon`}>
      <EventIcon fontSize="small" />
    </span>
    <span className={`GM__${theme}-event-title`}>{event.title}</span>
  </div>
);

const AddVehicleButton = ({
  theme,
  date,
  onAddVehicle,
  isPastDate,
  isToday,
  routes = [],
  selectedRouteId,
  isEventBlocked = false, // Nueva prop para manejar eventos
}) => {
  const { showNotification } = useNotification();

  // Verificar si es día futuro (no pasado y no hoy)
  const isFutureDate = !isPastDate && !isToday;

  // Solo permitir añadir en días futuros y sin eventos bloqueantes
  if (!isFutureDate || isEventBlocked) return null;

  const canAddVehicle = () => {
    if (routes.length === 0) return false;
    if (routes.length === 1) return true;
    return !!selectedRouteId;
  };

  const handleClick = () => {
    if (!canAddVehicle()) {
      let message = "No se puede añadir vehículo";

      if (routes.length === 0) {
        message = "No hay rutas configuradas para esta operación";
      } else if (routes.length > 1 && !selectedRouteId) {
        message = "Selecciona una ruta antes de añadir vehículos";
      }

      showNotification({
        message,
        severity: "warning",
      });
      return;
    }

    onAddVehicle(date);
  };

  const isDisabled = !canAddVehicle();

  return (
    <button
      className={`GM__${theme}-add-vehicle-btn ${isDisabled ? "disabled" : ""}`}
      onClick={handleClick}
      disabled={isDisabled}
      title={
        isDisabled
          ? "Selecciona una ruta para continuar"
          : "Añadir nuevo vehículo"
      }
      style={{
        opacity: isDisabled ? 0.5 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
    >
      <span className={`GM__${theme}-btn-icon`}>+</span>
      <span className={`GM__${theme}-btn-text`}>Añadir vehículo</span>
    </button>
  );
};

const WeekCalendar = ({
  theme,
  weekNumber,
  days,
  onDragEnd,
  onDeleteVehicle,
  onCancelVehicle,
  onEventClick,
  onVehicleAdd,
  isPastDate,
  isToday,
  renderCapacityIndicator,
  routes = [],
  selectedRouteId,
  operationId,
  onExportDay,
}) => {
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEventDialogOpen(true);
  };

  const handleCloseEventDialog = () => {
    setEventDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleEventDetailClick = () => {
    if (selectedEvent && onEventClick) {
      onEventClick(selectedEvent);
      handleCloseEventDialog();
    }
  };

  return (
    <div className={`GM__${theme}-week-container`}>
      <div className={`GM__${theme}-week-header`}>
        <span className={`GM__${theme}-week-title`}>Semana {weekNumber}</span>
      </div>

      <div className={`GM__${theme}-days-container`}>
        <div className={`GM__${theme}-days-header`}>
          {days.map((day) => {
            const date = day.date ? formatDateToISOString(day.date) : null;
            const dayIsPast = isPastDate && date ? isPastDate(date) : false;
            const dayIsToday = isToday && date ? isToday(date) : false;
            
            // Filtrar vehículos activos (no cancelados) para el día
            const activeVehicles = day.vehicles ? 
              day.vehicles.filter(v => !v.is_cancelled && !v.isPredicted) : [];
            
            // Determinar si mostrar el botón de exportación
            const showExportButton = date && 
              (dayIsPast || dayIsToday) && 
              activeVehicles.length > 0;

            return (
              <div
                key={`header-${day.dayName}`}
                className={`GM__${theme}-day-header ${
                  dayIsPast ? "past-date" : ""
                } ${dayIsToday ? "today" : ""} ${
                  day.hasCapacityIssue ? "has-capacity-issue" : ""
                } ${
                  day.hasPredictionConflicts ? "has-prediction-conflicts" : ""
                } ${day.hasFaltantes ? "has-faltantes" : ""}`}
                style={{ position: 'relative' }} // Importante para posicionamiento absoluto
              >
                <div className={`GM__${theme}-day-name`}>{day.dayName}</div>
                {day.date && (
                  <>
                    <div className={`GM__${theme}-day-date`}>
                      {day.formattedDate}
                    </div>
                    {day.capacity && (
                      <div className={`GM__${theme}-day-capacity`}>
                        <CapacityIndicator
                          capacity={day.capacity}
                          vehicles={day.vehicles || []}
                          theme={theme}
                          selectedRouteId={selectedRouteId}
                        />
                      </div>
                    )}
                    <PredictionStatusIndicator day={day} theme={theme} />
                    
                    {/* Botón de exportación */}
                    {showExportButton && (
                      <ExportButton
                        theme={theme}
                        date={date}
                        vehicles={activeVehicles}
                        routeId={selectedRouteId}
                        routeName={routes.find(r => r.id === selectedRouteId)?.route_name || 'Sin Ruta'}
                        operationId={operationId}
                        onExport={onExportDay}
                      />
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <div className={`GM__${theme}-days-content`}>
            {days.map((day) => {
              const date = day.date ? formatDateToISOString(day.date) : null;
              const dayIsPast = isPastDate && date ? isPastDate(date) : false;
              const dayIsToday = isToday && date ? isToday(date) : false;
              const hasPredictions =
                day.vehicles && day.vehicles.some((v) => v.isPredicted);
              const hasEvents = day.events && day.events.length > 0;
              const isEventBlocked = day.isBlockedByEvent || false;
              const hasCapacityIssue =
                day.hasCapacityIssue ||
                (day.capacity &&
                  (day.capacity.isOverCapacity ||
                    day.capacity.isUnderCapacity));

              return (
                <div
                  key={`day-${day.dayName}`}
                  className={`GM__${theme}-day-column ${
                    !day.date ? "empty" : ""
                  } ${dayIsPast ? "past-date" : ""} ${
                    dayIsToday ? "today" : ""
                  } ${hasPredictions ? "has-predictions" : ""} ${
                    hasEvents ? "has-events" : ""
                  } ${hasCapacityIssue ? "has-capacity-issue" : ""} ${
                    day.hasPredictionConflicts ? "has-prediction-conflicts" : ""
                  } ${day.hasFaltantes ? "has-faltantes" : ""} ${
                    isEventBlocked ? "event-blocked" : ""
                  }`}
                >
                  {/* Indicador de día bloqueado */}
                  {isEventBlocked && (
                    <div className={`GM__${theme}-event-blocked-tooltip`}>
                      {day.blockedMessage}
                    </div>
                  )}

                  {/* Indicador de vehículos diferidos */}
                  {day.hasDeferredVehicles && (
                    <div className={`GM__${theme}-deferred-indicator`}>
                      <span className={`GM__${theme}-deferred-icon`}>↻</span>
                      Vehículos reprogramados
                    </div>
                  )}

                  {day.events && day.events.length > 0 && (
                    <div className={`GM__${theme}-day-events`}>
                      {day.events.map((event) => (
                        <EventBadge
                          key={event.id}
                          theme={theme}
                          event={event}
                          onClick={handleEventClick}
                        />
                      ))}
                    </div>
                  )}

                  {hasCapacityIssue && day.capacity && (
                    <div className={`GM__${theme}-capacity-alert`}>
                      {day.capacity.isOverCapacity && (
                        <Tooltip
                          title={`Excedido por ${
                            day.capacity.difference || 0
                          } vehículos`}
                        >
                          <Chip
                            icon={<ErrorOutlineIcon />}
                            label="Sobre capacidad"
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {day.capacity.isUnderCapacity && (
                        <Tooltip
                          title={`Faltan ${Math.abs(
                            day.capacity.difference || 0
                          )} vehículos`}
                        >
                          <Chip
                            icon={<WarningAmberIcon />}
                            label="Bajo capacidad"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {date && (
                    <Droppable
                      droppableId={date}
                      isDropDisabled={isEventBlocked || dayIsPast || dayIsToday}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`GM__${theme}-day-content ${
                            snapshot.isDraggingOver ? "dragging-over" : ""
                          } ${dayIsPast ? "past-date" : ""} ${
                            hasCapacityIssue ? "capacity-issue" : ""
                          } ${
                            day.hasPredictionConflicts
                              ? "prediction-conflicts"
                              : ""
                          } ${day.hasFaltantes ? "has-faltantes" : ""}`}
                        >
                          {/* ACTUALIZACIÓN CRÍTICA: Mostrar botón independientemente del estado del Droppable */}
                          <div className={`GM__${theme}-day-actions`}>
                            <AddVehicleButton
                              theme={theme}
                              date={date}
                              onAddVehicle={onVehicleAdd}
                              isPastDate={dayIsPast}
                              isToday={dayIsToday}
                              routes={routes}
                              selectedRouteId={selectedRouteId}
                              isEventBlocked={isEventBlocked} // Nueva prop agregada
                            />
                          </div>

                          {day.vehicles &&
                            day.vehicles.map((vehicle, index) => {
                              const isDeferredVehicle =
                                vehicle.isDeferredFromEvent || false;

                              return (
                                <Draggable
                                  key={vehicle.id}
                                  draggableId={vehicle.id.toString()}
                                  index={index}
                                  isDragDisabled={
                                    dayIsPast ||
                                    dayIsToday ||
                                    !vehicle.isActive ||
                                    vehicle.isPredicted ||
                                    isEventBlocked
                                  }
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`GM__${theme}-vehicle-item ${
                                        snapshot.isDragging ? "dragging" : ""
                                      } ${
                                        !vehicle.isActive ? "disabled" : ""
                                      } ${
                                        vehicle.isPredicted ? "predicted" : ""
                                      } ${dayIsToday ? "today-vehicle" : ""} ${
                                        isDeferredVehicle ? "deferred" : ""
                                      }`}
                                      style={{
                                        ...provided.draggableProps.style,
                                        opacity: !vehicle.isActive
                                          ? 0.6
                                          : vehicle.isPredicted
                                          ? 0.8
                                          : isEventBlocked
                                          ? 0.5
                                          : 1,
                                        backgroundColor: vehicle.isPredicted
                                          ? "#f2f9e8"
                                          : isDeferredVehicle
                                          ? "rgba(255, 152, 0, 0.1)"
                                          : null,
                                        border: vehicle.isPredicted
                                          ? "1px dashed #4caf50"
                                          : isDeferredVehicle
                                          ? "1px dashed #ff9800"
                                          : null,
                                      }}
                                    >
                                      <div
                                        className={`GM__${theme}-vehicle-plate`}
                                      >
                                        {vehicle.plate}
                                        {vehicle.isPredicted &&
                                          !isDeferredVehicle && (
                                            <span className="predicted-badge">
                                              {" "}
                                              (Predicción)
                                            </span>
                                          )}
                                        {isDeferredVehicle && (
                                          <span className="deferred-badge">
                                            {" "}
                                            (Reprogramado)
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        className={`GM__${theme}-vehicle-operation`}
                                      >
                                        {vehicle.operation || "No asignada"}
                                      </div>
                                      {!vehicle.isActive && (
                                        <div className={`GM__${theme}-tooltip`}>
                                          <button
                                            className={`GM__${theme}-icon-button GM__${theme}-vehicle-info-btn`}
                                          >
                                            <InfoIcon fontSize="small" />
                                          </button>
                                          <span
                                            className={`GM__${theme}-tooltip-text`}
                                          >
                                            <div
                                              className={`GM__${theme}-tooltip-title`}
                                            >
                                              Vehículo deshabilitado
                                            </div>
                                            <div
                                              className={`GM__${theme}-tooltip-detail`}
                                            >
                                              {vehicle.disabledReason ||
                                                "Sin motivo especificado"}
                                            </div>
                                          </span>
                                        </div>
                                      )}
                                      <div
                                        className={`GM__${theme}-vehicle-actions`}
                                      >
                                        {!dayIsPast &&
                                          !dayIsToday &&
                                          !vehicle.isPredicted && (
                                            <button
                                              className={`GM__${theme}-icon-button GM__${theme}-vehicle-delete-btn`}
                                              onClick={() =>
                                                onDeleteVehicle(vehicle.id)
                                              }
                                              title="Eliminar vehículo"
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </button>
                                          )}
                                        {!vehicle.isPredicted && dayIsToday && (
                                          <button
                                            className={`GM__${theme}-icon-button GM__${theme}-vehicle-cancel-btn`}
                                            onClick={() =>
                                              onCancelVehicle(vehicle.id, {
                                                vehicleId: vehicle.vehicleId,
                                                plate: vehicle.plate,
                                                date: date,
                                                operation: vehicle.operation,
                                              })
                                            }
                                            title="Cancelar vehículo"
                                          >
                                            <Cancel fontSize="small" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}

                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  )}
                </div>
              );
            })}
          </div>
        </DragDropContext>
      </div>

      {eventDialogOpen && selectedEvent && (
        <div className={`GM__${theme}-modal-backdrop`}>
          <div className={`GM__${theme}-modal-content`}>
            <div className={`GM__${theme}-modal-header`}>
              <h3 className={`GM__${theme}-modal-title`}>
                {selectedEvent.title}
              </h3>
            </div>
            <div className={`GM__${theme}-modal-body`}>
              <div className={`GM__${theme}-modal-subtitle`}>
                {selectedEvent.isFirstDay && selectedEvent.isLastDay
                  ? "Evento de un día"
                  : selectedEvent.isFirstDay
                  ? "Fecha de inicio"
                  : selectedEvent.isLastDay
                  ? "Fecha de fin"
                  : "Evento en progreso"}
                {selectedEvent.isFullDay && " - Todo el día"}
              </div>

              {selectedEvent.description && (
                <div className={`GM__${theme}-modal-description`}>
                  {selectedEvent.description}
                </div>
              )}
            </div>
            <div className={`GM__${theme}-modal-footer`}>
              <button
                className={`GM__${theme}-button GM__${theme}-button-secondary`}
                onClick={handleCloseEventDialog}
              >
                Cerrar
              </button>
              {onEventClick && (
                <button
                  className={`GM__${theme}-button GM__${theme}-button-primary`}
                  onClick={handleEventDetailClick}
                >
                  Ver Detalles
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// PropTypes actualizados con precisión técnica
PredictionStatusIndicator.propTypes = {
  day: PropTypes.shape({
    hasPredictionConflicts: PropTypes.bool,
    predictionConflicts: PropTypes.arrayOf(PropTypes.string),
    hasFaltantes: PropTypes.bool,
    faltantesCount: PropTypes.number,
  }).isRequired,
  theme: PropTypes.string.isRequired,
};

CapacityIndicator.propTypes = {
  capacity: PropTypes.shape({
    planned: PropTypes.number,
    plannedCapacity: PropTypes.number,
    scheduled: PropTypes.number,
    scheduledCount: PropTypes.number,
    cancelled: PropTypes.number,
    cancelledCount: PropTypes.number,
    status: PropTypes.string,
    difference: PropTypes.number,
    isOverCapacity: PropTypes.bool,
    isUnderCapacity: PropTypes.bool,
    operationRouteId: PropTypes.string,
  }),
  vehicles: PropTypes.arrayOf(PropTypes.object),
  theme: PropTypes.string.isRequired,
  selectedRouteId: PropTypes.string,
};

ExportButton.propTypes = {
  theme: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  vehicles: PropTypes.array.isRequired,
  routeId: PropTypes.string,
  routeName: PropTypes.string,
  operationId: PropTypes.string.isRequired,
  onExport: PropTypes.func.isRequired,
};

VehicleItem.propTypes = {
  theme: PropTypes.string.isRequired,
  vehicle: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    vehicleId: PropTypes.string,
    plate: PropTypes.string.isRequired,
    operation: PropTypes.string,
    isActive: PropTypes.bool,
    isCancelled: PropTypes.bool,
    isPredicted: PropTypes.bool,
    disabledReason: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  isPastDate: PropTypes.bool,
  isToday: PropTypes.bool,
  date: PropTypes.string.isRequired,
};

EventBadge.propTypes = {
  theme: PropTypes.string.isRequired,
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    isFullDay: PropTypes.bool,
    isFirstDay: PropTypes.bool,
    isLastDay: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

AddVehicleButton.propTypes = {
  theme: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  onAddVehicle: PropTypes.func.isRequired,
  isPastDate: PropTypes.bool,
  isToday: PropTypes.bool,
  routes: PropTypes.array,
  selectedRouteId: PropTypes.string,
  isEventBlocked: PropTypes.bool,
};

WeekCalendar.propTypes = {
  theme: PropTypes.string.isRequired,
  weekNumber: PropTypes.number.isRequired,
  days: PropTypes.arrayOf(
    PropTypes.shape({
      dayName: PropTypes.string.isRequired,
      date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      formattedDate: PropTypes.string,
      vehicles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          plate: PropTypes.string.isRequired,
          operation: PropTypes.string,
          isActive: PropTypes.bool,
          isPredicted: PropTypes.bool,
          disabledReason: PropTypes.string,
        })
      ),
      events: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          title: PropTypes.string.isRequired,
          description: PropTypes.string,
          isFullDay: PropTypes.bool,
          isFirstDay: PropTypes.bool,
          isLastDay: PropTypes.bool,
        })
      ),
      capacity: PropTypes.shape({
        planned: PropTypes.number,
        plannedCapacity: PropTypes.number,
        scheduled: PropTypes.number,
        scheduledCount: PropTypes.number,
        cancelled: PropTypes.number,
        cancelledCount: PropTypes.number,
        status: PropTypes.string,
        difference: PropTypes.number,
        isOverCapacity: PropTypes.bool,
        isUnderCapacity: PropTypes.bool,
      }),
      hasCapacityIssue: PropTypes.bool,
      hasPredictionConflicts: PropTypes.bool,
      predictionConflicts: PropTypes.arrayOf(PropTypes.string),
      hasFaltantes: PropTypes.bool,
      faltantesCount: PropTypes.number,
    })
  ).isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDeleteVehicle: PropTypes.func.isRequired,
  onCancelVehicle: PropTypes.func,
  onEventClick: PropTypes.func,
  onVehicleAdd: PropTypes.func.isRequired,
  isPastDate: PropTypes.func.isRequired,
  isToday: PropTypes.func,
  renderCapacityIndicator: PropTypes.func,
  routes: PropTypes.array,
  selectedRouteId: PropTypes.string,
  operationId: PropTypes.string.isRequired,
  onExportDay: PropTypes.func.isRequired,
};

export default WeekCalendar;